import { call, put, takeLatest } from "redux-saga/effects";
import { types } from "reducers/metrocorp";
import * as metrocorpMiddleware from "middleware/metrocorp";
import moment from "moment";
import { actions as notificationActions } from "reducers/notification";
import * as i18n from "util/i18n";
import { downloadCsv, downloadPdf, downloadXls } from "util/download";
import { delay } from "redux-saga";
import * as configUtils from "util/config";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";

const DOWNLOAD_LIST_SCOPES = ["metrocorp.list"];
const DOWNLOAD_SUCCESS_KEY = "metrocorp.operations.download.success";
const DOWNLOAD_ERROR_KEY = "metrocorp.operations.download.error";
const DOWNLOAD_SUCCESS_FALLBACK = "Confirmado. Archivo descargado";
const DOWNLOAD_ERROR_FALLBACK = "Error. Ha ocurrido un error procesando la solicitud";

const sagas = [
    takeLatest(types.LIST_METROCORP_REQUEST, listMetrocorp),
    takeLatest(types.LIST_METROCORP_PRE_REQUEST, listMetrocorpPre),
    takeLatest(types.DOWNLOAD_LIST_METROCORP_REQUEST, downloadList),
    takeLatest(types.DOWNLOAD_PERFORMANCE_METROCORP_REQUEST, downloadPerformance),
    takeLatest(types.DOWNLOAD_DETAIL_METROCORP_REQUEST, downloadDetail),
    takeLatest(types.GET_DETAIL_MOVEMENT_REQUEST, getDetail),
    takeLatest(types.DOWNLOAD_DETAIL_METROCORP_BY_REFERENCE_REQUEST, downloadDetailByReference),
    takeLatest(types.DOWNLOAD_REPORT_DRAWER_REQUEST, downloadMonthlyReport),
];

export default sagas;
function getGenericMsj() {
    const genericErrorMsj = configUtils.get(
        "frontend.metrocorp.generic.error.message",
        "Ha ocurrido un error procesando la solicitud.",
    );
    return genericErrorMsj;
}
function* listMetrocorp({
    optionSelected,
    principalAccount,
    species,
    date,
    dateFrom,
    dateTo,
    page,
    isDesktop,
    directionFilter,
}) {
    const response = yield call(
        metrocorpMiddleware.listMetrocorp,
        optionSelected,
        principalAccount,
        species,
        date,
        dateFrom,
        dateTo,
        page,
        directionFilter,
    );

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["metrocorp.list"]));
        yield put({ type: types.LIST_METROCORP_FAILURE });
    } else {
        const {
            holdings,
            futureValues,
            movements,
            fullMovementsList,
            fullMovementsListToDownload,
            dateHoldings,
            totalPages,
        } = response.data.data;

        yield put({
            type: types.LIST_METROCORP_SUCCESS,
            holdings,
            futureValues,
            movements,
            fullMovementsList,
            fullMovementsListToDownload,
            dateHoldings,
            movementsTotalPages: totalPages,
            futureValuesTotalPages: totalPages,
            page,
            isDesktop,
            directionFilter,
        });
    }
}

function* listMetrocorpPre() {
    const response = yield call(metrocorpMiddleware.listMetrocorpPre);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["metrocorp.list"]));
        yield put({ type: types.LIST_METROCORP_PRE_FAILURE });
    } else {
        const { holdings, clients, activeTotal } = response.data.data;

        yield put({
            type: types.LIST_METROCORP_PRE_SUCCESS,
            holdings,
            clients,
            activeTotal,
            filtersData: {
                principalAccountClient: `${clients[0]?.nombre} ${clients[0]?.apellido}`,
                principalAccount: clients[0]?.codClie || "",
                species: "all",
                date: moment(),
                page: 1,
            },
        });
    }
}

function* notifyDownloadListError() {
    yield put({ type: types.DOWNLOAD_LIST_METROCORP_FAILURE });
    // CA ARBCMFIMP-19408: snackbar de error persistente.
    yield put(
        notificationActions.showNotification(
            i18n.get(DOWNLOAD_ERROR_KEY, DOWNLOAD_ERROR_FALLBACK),
            "error",
            DOWNLOAD_LIST_SCOPES,
            null,
            false,
        ),
    );
}

function* downloadList({ summary, format }) {
    try {
        // Códigos *E los tira el interceptor de API; sin catch el spinner queda en true.
        const { type, data } = yield call(metrocorpMiddleware.downloadList, summary, format);

        if (type === "W") {
            yield* notifyDownloadListError();
            return;
        }

        const content = data?.data?.content;
        const fileName = data?.data?.fileName;
        if (!content || !fileName) {
            yield* notifyDownloadListError();
            return;
        }

        const normalizedFormat = (format || "pdf").toLowerCase();

        // Descargar primero; el toast de éxito solo si no falló (mismo orden que downloadPerformance).
        if (normalizedFormat === "pdf") {
            downloadPdf(fileName, content);
        } else if (normalizedFormat === "csv") {
            downloadCsv(fileName, content);
        } else {
            downloadXls(fileName, content);
        }

        // CA: snackbar de éxito no persistente.
        yield put(
            notificationActions.showNotification(
                i18n.get(DOWNLOAD_SUCCESS_KEY, DOWNLOAD_SUCCESS_FALLBACK),
                "success",
                DOWNLOAD_LIST_SCOPES,
                null,
                true,
            ),
        );

        yield put({ type: types.DOWNLOAD_LIST_METROCORP_SUCCESS });
    } catch (error) {
        // Mantener la pantalla (snackbar) en vez de /error; siempre liberar el botón.
        yield* notifyDownloadListError();
    }
}

function* notifyPerformanceDownloadFailure() {
    yield put({ type: types.DOWNLOAD_PERFORMANCE_METROCORP_FAILURE });
    yield put(
        notificationActions.showNotification(
            i18n.get("global.unexpectedError"),
            "error",
            ["metrocorp.list"],
            null,
            false,
        ),
    );
}

function* downloadPerformance({ account, date, verifyCode }) {
    try {
        const response = yield call(metrocorpMiddleware.downloadPerformance, account, date, verifyCode);

        if (response.type === "W") {
            yield* notifyPerformanceDownloadFailure();
            return;
        }

        const { content, fileName } = response.data.data;
        yield call(downloadXls, fileName, content);
        yield put(
            notificationActions.showNotification(
                i18n.get("global.successDownload"),
                "success",
                ["metrocorp.list"],
                null,
                false,
            ),
        );
        yield put({ type: types.DOWNLOAD_PERFORMANCE_METROCORP_SUCCESS });
    } catch {
        yield* notifyPerformanceDownloadFailure();
    }
}

function* getDetail({ reference, codEspe, nombre, apellido, currency, observaciones }) {
    const response = yield call(metrocorpMiddleware.getDetail, reference);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(getGenericMsj(), "error", ["metrocorp.list"]));
        yield put({ type: types.GET_DETAIL_MOVEMENT_FAILURE });
    } else {
        const { movementDetail } = response.data.data;

        const updatedMovementDetail = {
            ...movementDetail,
            codEspe,
            Nombre: nombre,
            Apellido: apellido,
            currency,
            observaciones,
        };
        yield put({
            type: types.GET_DETAIL_MOVEMENT_SUCCESS,
            movementDetail: updatedMovementDetail,
        });
    }
}

function* downloadDetail({ detail, format, onClose = () => {} }) {
    const { type, data } = yield call(metrocorpMiddleware.downloadDetail, detail, format);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_DETAIL_METROCORP_FAILURE });
        yield put(notificationActions.showNotification(getGenericMsj(), "error", ["metrocorp.detail"]));
    } else {
        onClose();
        yield put(
            notificationActions.showNotification(i18n.get("global.successDownload"), "success", ["metrocorp.list"]),
        );

        const { content, fileName } = data.data;

        if (format === "pdf") {
            downloadPdf(fileName, content);
        } else {
            downloadXls(fileName, content);
        }

        yield put({ type: types.DOWNLOAD_DETAIL_METROCORP_SUCCESS });
    }
}

function* downloadDetailByReference({ reference, codEspe, nombre, apellido, currency, format, observaciones }) {
    const response = yield call(
        metrocorpMiddleware.getDetail,
        reference,
        codEspe,
        nombre,
        apellido,
        currency,
        observaciones,
    );

    if (response.type === "W") {
        yield put(notificationActions.showNotification(getGenericMsj(), "error", ["metrocorp.list"]));
        yield put({ type: types.GET_DETAIL_MOVEMENT_FAILURE });
    } else {
        const { movementDetail } = response.data.data;
        const updatedMovementDetail = {
            ...movementDetail,
            codEspe,
            Nombre: nombre,
            Apellido: apellido,
            currency,
            observaciones,
        };
        const { type, data } = yield call(metrocorpMiddleware.downloadDetail, updatedMovementDetail, format);
        if (type === "W") {
            yield put({ type: types.DOWNLOAD_DETAIL_METROCORP_FAILURE });
            yield put(notificationActions.showNotification(getGenericMsj(), "error", ["metrocorp.detail"]));
        } else {
            yield call(delay, 2000);

            yield put(
                notificationActions.showNotification(i18n.get("global.successDownload"), "success", ["metrocorp.list"]),
            );
            const { content, fileName } = data.data;

            if (format === "pdf") {
                downloadPdf(fileName, content);
            } else {
                downloadXls(fileName, content);
            }

            yield put({ type: types.DOWNLOAD_DETAIL_METROCORP_BY_REFERENCE_SUCCESS });
        }
    }
}

function* downloadMonthlyReport({ reportDrawerValues }) {
    const { type, data } = yield call(metrocorpMiddleware.downloadMonthlyReport, reportDrawerValues);
    if (type === "W") {
        yield put({ type: types.DOWNLOAD_REPORT_DRAWER_FAILURE });
        yield put(
            notificationActions.showNotification(
                getGenericMsj(),
                "error",
                !isUxDashboardImprovementsEnabled() ? ["monthlyReportMetrocorpDrawer"] : ["metrocorp.list"],
                null,
                false,
            ),
        );
    } else {
        yield call(delay, 3000);

        const { content, fileName } = data.data;

        if (reportDrawerValues?.format.toUpperCase() === "PDF") {
            downloadPdf(fileName, content);
        } else {
            downloadXls(fileName, content);
        }

        yield put(
            notificationActions.showNotification(
                i18n.get("global.successDownload"),
                "success",
                !isUxDashboardImprovementsEnabled() ? ["monthlyReportMetrocorpDrawer"] : ["metrocorp.list"],
                null,
                false,
            ),
        );
        yield put({ type: types.DOWNLOAD_REPORT_DRAWER_SUCCESS });
    }
}
